
import React from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ChronicConditionsField from './fields/ChronicConditionsField';
import MedicationsField from './fields/MedicationsField';
import GynecologicalField from './fields/GynecologicalField';
import MentalHealthField from './fields/MentalHealthField';
import FormActions from './fields/FormActions';

const medicalHistorySchema = z.object({
  hasChronicConditions: z.boolean(),
  chronicConditionsDetails: z.string().optional(),
  takingMedications: z.boolean(),
  medicationsDetails: z.string().optional(),
  hadGynSurgery: z.boolean(),
  gynSurgeryDetails: z.string().optional(),
  experiencedDepression: z.boolean(),
  highStressLevels: z.boolean(),
  healthyDiet: z.boolean(),
  exerciseFrequency: z.string(),
});

interface MedicalHistoryEditProps {
  data: z.infer<typeof medicalHistorySchema>;
  onSave: (data: z.infer<typeof medicalHistorySchema>) => void;
  onCancel: () => void;
}

const MedicalHistoryEdit: React.FC<MedicalHistoryEditProps> = ({ data, onSave, onCancel }) => {
  const form = useForm<z.infer<typeof medicalHistorySchema>>({
    resolver: zodResolver(medicalHistorySchema),
    defaultValues: data
  });

  const handleSubmit = (values: z.infer<typeof medicalHistorySchema>) => {
    onSave(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Period Tracking Notice */}
        <div className="bg-hormony-beige/20 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-medium mb-2">Period Tracking</h3>
          <p className="text-muted-foreground mb-3">
            Period tracking is now available in the dedicated Cycle Tracking section.
            You can record your periods, view your cycle history, and get insights.
          </p>
          <Button asChild variant="outline">
            <Link to="/cycle-tracking">Go to Cycle Tracking</Link>
          </Button>
        </div>

        {/* Chronic Conditions */}
        <ChronicConditionsField form={form} />

        {/* Medications */}
        <MedicationsField form={form} />

        {/* Gynecological History */}
        <GynecologicalField form={form} />

        {/* Mental Health & Lifestyle */}
        <MentalHealthField form={form} />

        {/* Actions */}
        <FormActions onCancel={onCancel} />
      </form>
    </Form>
  );
};

export default MedicalHistoryEdit;
